import React, { memo, useState } from 'react';
import { ACCOUNT_TYPE } from '../../constants/ACCOUNT_TYPE';

/**
 * Modal form for manually adding debt accounts to the refinance calculator
 */
const ManualAccountForm = memo(({ onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
        name: '',
        balance: '',
        monthlyPayment: '',
        apr: '',
        type: ACCOUNT_TYPE.CREDIT_CARD,
        minimum_payment_percentage: 0.02, // Default 2%
        minimum_payment_low_balance: 25 // Default $25 minimum
    });

    const [errors, setErrors] = useState({});

    const debtAccountTypes = [
        ACCOUNT_TYPE.CREDIT_CARD,
        ACCOUNT_TYPE.LINE_OF_CREDIT,
        ACCOUNT_TYPE.LOAN,
        ACCOUNT_TYPE.STUDENT_LOAN,
        ACCOUNT_TYPE.OTHER
    ];

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Account name is required';
        }

        const balance = parseFloat(formData.balance);
        if (!formData.balance || isNaN(balance) || balance <= 0) {
            newErrors.balance = 'Balance must be a positive number';
        }

        const monthlyPayment = parseFloat(formData.monthlyPayment);
        if (!formData.monthlyPayment || isNaN(monthlyPayment) || monthlyPayment <= 0) {
            newErrors.monthlyPayment = 'Monthly payment must be a positive number';
        }

        const apr = parseFloat(formData.apr);
        if (formData.apr && (isNaN(apr) || apr < 0 || apr > 50)) {
            newErrors.apr = 'APR must be between 0 and 50';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        const accountData = {
            name: formData.name.trim(),
            balance: parseFloat(formData.balance),
            apr: formData.apr ? parseFloat(formData.apr) / 100 : 0, // Convert percentage to decimal
            type: formData.type,
            minimum_payment: parseFloat(formData.monthlyPayment),
            minimum_payment_percentage: formData.minimum_payment_percentage,
            minimum_payment_low_balance: formData.minimum_payment_low_balance,
            fixed_payment: null,
            institution: 'Manual Entry'
        };
        
        // Calculate the monthly payment using the same logic as real accounts
        accountData.monthly_payment = parseFloat(formData.monthlyPayment);
        accountData.defaultPaymentAmount = parseFloat(formData.monthlyPayment);
        
        onSubmit(accountData);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onCancel();
        }
    };

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
            }}
            onClick={handleBackdropClick}
        >
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '2rem',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
                <h3 style={{ margin: '0 0 1.5rem 0', color: '#333' }}>
                    Add Manual Account
                </h3>
                <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.95rem' }}>
                    Add a debt account manually to include in your refinance calculation.
                </p>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {/* Account Name */}
                    <div className="formField">
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            fontWeight: 'bold',
                            color: '#333'
                        }}>
                            Account Name *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="formField"
                            style={{ 
                                width: '100%',
                                border: errors.name ? '2px solid #d32f2f' : undefined
                            }}
                            placeholder="e.g., Chase Freedom Card"
                            maxLength={50}
                        />
                        {errors.name && (
                            <small style={{ color: '#d32f2f', fontSize: '0.85rem' }}>
                                {errors.name}
                            </small>
                        )}
                    </div>

                    {/* Account Type */}
                    <div className="formField">
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            fontWeight: 'bold',
                            color: '#333'
                        }}>
                            Account Type
                        </label>
                        <select
                            value={formData.type}
                            onChange={(e) => handleInputChange('type', e.target.value)}
                            className="formField"
                            style={{ width: '100%' }}
                        >
                            {debtAccountTypes.map(type => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Balance */}
                    <div className="formField">
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            fontWeight: 'bold',
                            color: '#333'
                        }}>
                            Current Balance *
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={formData.balance}
                            onChange={(e) => handleInputChange('balance', e.target.value)}
                            className="formField"
                            style={{ 
                                width: '100%',
                                border: errors.balance ? '2px solid #d32f2f' : undefined
                            }}
                            placeholder="e.g., 5000.00"
                        />
                        {errors.balance && (
                            <small style={{ color: '#d32f2f', fontSize: '0.85rem' }}>
                                {errors.balance}
                            </small>
                        )}
                    </div>

                    {/* Monthly Payment */}
                    <div className="formField">
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            fontWeight: 'bold',
                            color: '#333'
                        }}>
                            Monthly Payment *
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={formData.monthlyPayment}
                            onChange={(e) => handleInputChange('monthlyPayment', e.target.value)}
                            className="formField"
                            style={{ 
                                width: '100%',
                                border: errors.monthlyPayment ? '2px solid #d32f2f' : undefined
                            }}
                            placeholder="e.g., 150.00"
                        />
                        {errors.monthlyPayment && (
                            <small style={{ color: '#d32f2f', fontSize: '0.85rem' }}>
                                {errors.monthlyPayment}
                            </small>
                        )}
                    </div>

                    {/* APR */}
                    <div className="formField">
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            fontWeight: 'bold',
                            color: '#333'
                        }}>
                            Annual Percentage Rate (APR)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="50"
                            value={formData.apr}
                            onChange={(e) => handleInputChange('apr', e.target.value)}
                            className="formField"
                            style={{ 
                                width: '100%',
                                border: errors.apr ? '2px solid #d32f2f' : undefined
                            }}
                            placeholder="e.g., 18.99"
                        />
                        {errors.apr && (
                            <small style={{ color: '#d32f2f', fontSize: '0.85rem' }}>
                                {errors.apr}
                            </small>
                        )}
                        <small style={{ color: '#666', fontSize: '0.85rem' }}>
                            Enter as percentage (e.g., 18.5 for 18.5%). Optional - used for interest calculations.
                        </small>
                    </div>

                    {/* Minimum Payment Percentage */}
                    <div className="formField">
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            fontWeight: 'bold',
                            color: '#333'
                        }}>
                            Min Payment % (Optional)
                        </label>
                        <input
                            type="number"
                            step="0.001"
                            min="0.01"
                            max="0.1"
                            value={formData.minimum_payment_percentage}
                            onChange={(e) => handleInputChange('minimum_payment_percentage', e.target.value)}
                            className="formField"
                            style={{ width: '100%' }}
                            placeholder="e.g., 0.025"
                        />
                        <small style={{ color: '#666', fontSize: '0.85rem' }}>
                            Decimal format (e.g., 0.025 for 2.5%). Used for payment calculations.
                        </small>
                    </div>

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '1rem',
                        justifyContent: 'flex-end',
                        marginTop: '1rem'
                    }}>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="button button-outline"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="button button-blue"
                        >
                            Add Account
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
});

ManualAccountForm.displayName = 'ManualAccountForm';

export default ManualAccountForm;